import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserCog } from "lucide-react";
import { SidebarMenuItem, SidebarMenuButton } from "@/components/ui/sidebar";
import { supabase } from "@/integrations/supabase/client";

interface SidebarUserProfileProps {
  username: string;
  isCollapsed: boolean;
}

export const SidebarUserProfile = ({ username, isCollapsed }: SidebarUserProfileProps) => {
  const [linkedinPicture, setLinkedinPicture] = useState<string | null>(null);
  const [linkedinName, setLinkedinName] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('linkedin_picture, linkedin_name')
        .eq('id', user.id)
        .single();

      if (profile?.linkedin_picture) {
        setLinkedinPicture(profile.linkedin_picture);
      }
      if (profile?.linkedin_name) {
        setLinkedinName(profile.linkedin_name);
      }
    };

    fetchProfile();
  }, []);

  const renderAvatar = () => {
    if (linkedinPicture) {
      return (
        <img 
          src={linkedinPicture} 
          alt={username}
          className="w-full h-full rounded-full object-cover"
        />
      );
    }
    return (
      <span className="text-sidebar-accent-foreground text-xs font-medium">
        {username.charAt(0).toUpperCase()}
      </span>
    );
  };

  if (isCollapsed) {
    return (
      <Link 
        to="/user-settings" 
        className="flex items-center justify-center w-full p-1 hover:opacity-80 transition-opacity"
      >
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-sidebar-accent aspect-square overflow-hidden">
          {renderAvatar()}
        </div>
      </Link>
    );
  }

  return (
    <SidebarMenuItem className="list-none">
      <SidebarMenuButton
        asChild
        tooltip="User Settings"
      >
        <Link to="/user-settings" className="flex items-center gap-2 p-1">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-sidebar-accent aspect-square overflow-hidden">
            {renderAvatar()}
          </div>
          <span className="truncate">{linkedinName || username}</span>
          <UserCog className="h-4 w-4 ml-auto" />
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};